import { createClient } from "@supabase/supabase-js";
import { SUPABASE_URL, SUPABASE_ANON_KEY } from "../config";

// Episode Editor sign-in (2026-09-28).
//
// Every team member has a Supabase Auth account made by
// scripts/editor_users.py: username -> <username>@editors.kathakala.us, the
// display name in user_metadata.name, and the role ("admin" | "volunteer") in
// app_metadata.role — app_metadata is only writable with the service key, so
// a volunteer cannot promote themselves from the browser.
//
// Two clients, deliberately:
//   authClient — holds the signed-in session (its own storage key).
//   dataClientFor(profile) — does the reads/writes, as anon (the same access
//     the other admin pages use today), tagged with x-kk-editor-* headers so
//     the editor_audit trigger records WHO changed each field. Keeping data on
//     anon means signing in cannot break row-level security the other pages
//     rely on.

export const EDITOR_EMAIL_DOMAIN = "editors.kathakala.us";
export const EDITOR_ROLES = ["admin", "volunteer"];

export const authClient = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: { storageKey: "kk-editor-auth", persistSession: true, autoRefreshToken: true, detectSessionInUrl: false },
});

export function usernameToEmail(username) {
  const u = String(username || "").trim().toLowerCase();
  return u.includes("@") ? u : `${u}@${EDITOR_EMAIL_DOMAIN}`;
}

export function profileFromUser(user) {
  if (!user) return null;
  const username = user.user_metadata?.username || (user.email || "").split("@")[0];
  return {
    id: user.id,
    username,
    name: user.user_metadata?.name || username,
    role: user.app_metadata?.role || null,
  };
}

export const canEdit = (p) => !!p && EDITOR_ROLES.includes(p.role);
export const isAdmin = (p) => p?.role === "admin";

const dataClients = {};
export function dataClientFor(profile) {
  if (!dataClients[profile.id]) {
    dataClients[profile.id] = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
      auth: { storageKey: `kk-editor-data-${profile.id}`, persistSession: false, autoRefreshToken: false, detectSessionInUrl: false },
      // HTTP headers must be ASCII, and names are often Telugu: URI-encode.
      global: { headers: { "x-kk-editor-id": profile.id, "x-kk-editor-name": encodeURIComponent(profile.name) } },
    });
  }
  return dataClients[profile.id];
}

export function decodeEditorName(raw) {
  if (!raw) return "";
  try { return decodeURIComponent(raw); } catch { return raw; }
}

// Stable per-person colour for presence dots and lock badges.
const PALETTE = ["#f59e0b", "#38bdf8", "#a78bfa", "#34d399", "#f472b6", "#fb7185", "#facc15", "#60a5fa", "#4ade80", "#c084fc"];
export function colorFor(id) {
  let h = 0;
  for (const ch of String(id || "")) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return PALETTE[h % PALETTE.length];
}
